import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, Loader2, Sparkles, Wand2, RotateCcw, Download, Lock,
  ShieldCheck, Coins, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';

function AuthImg({ genId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/avatar-studio/generations/${genId}/image`, { responseType: 'blob' });
        url = window.URL.createObjectURL(r.data);
        if (!cancelled) setSrc(url);
      } catch { /* leave blank */ }
    })();
    return () => { cancelled = true; if (url) window.URL.revokeObjectURL(url); };
  }, [genId]);
  if (!src) return <Loader2 className="w-5 h-5 animate-spin text-slate-300" />;
  return <img src={src} alt="Generated content" className="w-full h-full object-cover" data-testid={`gen-img-${genId}`} />;
}

export default function AvatarStudio() {
  const navigate = useNavigate();
  const { avatarId } = useParams();
  const [boot, setBoot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [controls, setControls] = useState({});
  const [aspect, setAspect] = useState('4:5');
  const [generating, setGenerating] = useState(false);
  const [gens, setGens] = useState([]);
  const [buying, setBuying] = useState(false);

  const loadBoot = useCallback(async () => {
    try { const r = await api.get(`/avatar-studio/avatars/${avatarId}`); setBoot(r.data); setAspect(r.data.controls.aspect_ratios[0] || '1:1'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not open studio'); navigate('/client/avatars'); }
    finally { setLoading(false); }
  }, [avatarId, navigate]);

  const loadGens = useCallback(async () => {
    try { const r = await api.get(`/avatar-studio/avatars/${avatarId}/generations`); setGens(r.data.generations); } catch { /* */ }
  }, [avatarId]);

  useEffect(() => { loadBoot(); loadGens(); }, [loadBoot, loadGens]);

  const setCtl = (k, v) => setControls((c) => ({ ...c, [k]: v }));
  const reset = () => { setControls({}); toast.message('Reset to the avatar\u2019s original look'); };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/avatar-studio/avatars/${avatarId}/generate`, {
        controls, aspect_ratio: aspect,
      });
      setBoot((b) => ({ ...b, capacity: r.data.capacity }));
      const g = r.data.generation;
      if (g.under_review) toast.warning('Submitted for review', { description: 'This image needs a quick safety check before it\u2019s available.' });
      else toast.success('New content generated');
      await loadGens();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402) toast.error('Out of generations', { description: d });
      else toast.error(d || 'Generation failed \u2014 you were not charged');
    } finally { setGenerating(false); }
  };

  const buyCredits = async () => {
    setBuying(true);
    try {
      const r = await api.post('/avatar-studio/credits/checkout', {
        origin_url: window.location.origin, packs: 1,
      });
      window.location.href = r.data.checkout_url;
    } catch { toast.error('Could not start checkout'); setBuying(false); }
  };

  const downloadImg = async (genId) => {
    try {
      const r = await api.get(`/avatar-studio/generations/${genId}/image`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `ievntur-avatar-${genId.slice(0, 8)}.png`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download image'); }
  };

  if (loading || !boot) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>;
  const cap = boot.capacity;
  const av = boot.avatar;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="avatar-studio-page">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button onClick={() => navigate('/client/avatars')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> My Avatars
        </button>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            {av.avatar_url && <img src={av.avatar_url} alt={av.name} className="w-14 h-14 rounded-full object-cover border-2 border-violet-200" />}
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-600" /> {av.name}</h1>
              <p className="text-xs text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Identity locked · restyle freely below</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs text-slate-400 uppercase tracking-wide">This month</div>
              <div className="text-sm font-semibold text-slate-800" data-testid="allowance-remaining">{cap.allowance_remaining} / {cap.monthly_allowance} included</div>
              <div className="text-xs text-slate-500" data-testid="credit-balance"><Coins className="w-3 h-3 inline" /> {cap.credits} credits</div>
            </div>
            <Button variant="outline" onClick={buyCredits} disabled={buying} data-testid="buy-credits-btn">
              {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}<span className="ml-1">Buy credits</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Controls */}
          <div className="lg:col-span-2 space-y-4">
            <Card><CardContent className="p-4 space-y-4">
              {boot.controls.groups.map((grp) => (
                <div key={grp.group}>
                  <div className="text-xs font-semibold uppercase tracking-wide text-violet-600 mb-2">{grp.group}</div>
                  <div className="space-y-3">
                    {grp.controls.map((c) => (
                      <div key={c.key}>
                        <label className="text-xs text-slate-500">{c.label}</label>
                        <Input value={controls[c.key] || ''} placeholder={c.suggestions?.[0] || ''}
                          onChange={(e) => setCtl(c.key, e.target.value)} data-testid={`ctl-${c.key}`} className="mt-1" />
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(c.suggestions || []).slice(0, 5).map((s) => (
                            <button key={s} onClick={() => setCtl(c.key, s)}
                              className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-violet-100 text-slate-600"
                              data-testid={`sugg-${c.key}-${s.replace(/\s+/g, '-')}`}>{s}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent></Card>
          </div>

          {/* Generate + result area */}
          <div className="lg:col-span-3 space-y-4">
            <Card><CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs text-slate-500">Aspect ratio:</span>
                {boot.controls.aspect_ratios.map((a) => (
                  <button key={a} onClick={() => setAspect(a)}
                    className={`text-xs px-3 py-1 rounded-full border ${aspect === a ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200'}`}
                    data-testid={`aspect-${a.replace(':', '-')}`}>{a}</button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button onClick={generate} disabled={generating || !cap.can_generate} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="generate-btn">
                  {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Generating…</> : <><Wand2 className="w-4 h-4 mr-1" /> Generate content</>}
                </Button>
                <Button variant="outline" onClick={reset} title="Reset to original" data-testid="reset-btn"><RotateCcw className="w-4 h-4" /></Button>
              </div>
              {!cap.can_generate && <p className="text-xs text-rose-600 mt-2">You’ve used your included generations. Buy credits to keep going.</p>}
            </CardContent></Card>

            {/* Gallery */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="studio-gallery">
              {gens.map((g) => (
                <Card key={g.id} className="overflow-hidden" data-testid={`gen-${g.id}`}>
                  <div className="aspect-[4/5] bg-slate-100 relative flex items-center justify-center">
                    {g.downloadable ? (
                      <AuthImg genId={g.id} />
                    ) : g.under_review ? (
                      <div className="text-center px-2" data-testid={`gen-review-${g.id}`}>
                        <ShieldCheck className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                        <p className="text-[11px] text-amber-700">Under review</p>
                      </div>
                    ) : (
                      <div className="text-center px-2"><ImageIcon className="w-6 h-6 text-slate-300 mx-auto" /><p className="text-[11px] text-slate-400">{g.rejected ? 'Not available' : 'Unavailable'}</p></div>
                    )}
                  </div>
                  <CardContent className="p-2 flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px]">{g.aspect_ratio}</Badge>
                    {g.downloadable && (
                      <button onClick={() => downloadImg(g.id)}
                        className="text-violet-600 hover:text-violet-700" data-testid={`download-${g.id}`}>
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                  </CardContent>
                </Card>
              ))}
              {!gens.length && <div className="col-span-full text-center text-slate-400 py-10 text-sm">No content yet — set a look and generate.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
